// =============================================================================
// PPIChao — Avaliação de chão: a worklist + o preenchimento (a etapa que FECHA o PPI)
// =============================================================================
// Duas telas:
//   1) WORKLIST — quem foi aprovado na prova e precisa ser avaliado na operação.
//      Mostra TODOS, inclusive quem ainda não fez 30 dias ("faltam X dias"), e o
//      botão de avaliar FUNCIONA SEMPRE: a janela é sugestão, não trava (decisão
//      da Julia). Sumir com a pessoa da tela até o 30º dia só criaria controle
//      por fora — o Treinamento precisa enxergar o que vem pela frente.
//   2) AVALIAÇÃO — o formulário (a versão publicada da trilha), Sim/Não/N/A por
//      pergunta, e a DECISÃO do RH (a nota informa; quem aprova é a pessoa).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    ArrowLeft, ClipboardCheck, Clock, CheckCircle2, XCircle, MinusCircle,
    Save, Send, AlertTriangle, UserMinus,
} from 'lucide-react';
import { CORES, BadgeStatus, Botao, CartaoKpi, estilos, usePaginacao, RodapePaginacao } from './ppiUI';
// A desistência é a MESMA ação nas três abas (Triagem, Aplicação e LIVE) — o modal,
// as chamadas e a marca cinza moram num lugar só desde 2026-08-06.
import {
    ehDesistente, ModalDesistencia, MarcaDesistiu, BotaoDesistir, BotaoDesfazerDesistencia,
} from './PPIDesistencia';
import useOrdenacaoTabela from '../../financeiro/useOrdenacaoTabela';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

const API = '/api/treinamento/ppi/chao';

const ITENS_POR_PAGINA = 10;

// Tipo de cada coluna ordenável (ver useOrdenacaoTabela). Fora do componente
// para a identidade ser estável e o useMemo do hook não re-ordenar a cada render.
const TIPOS_ORDENACAO = {
    NOME: 'texto', NOME_PROCESSO: 'texto', LOJA_ATUAL: 'texto', VAGA: 'texto',
    _DIAS: 'numero', _ESTADO: 'numero',
};

// Valores distintos de um campo, em ordem alfabética — alimenta os selects de filtro.
const opcoes = (linhas, campo) =>
    [...new Set(linhas.map((l) => l[campo]).filter(Boolean))]
        .sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));

// Quem saiu do processo por vontade própria. O desistente CONTINUA na lista,
// marcado — sumir com ele repetiria o problema que a desistência veio resolver
// (antes, para tirar alguém da tela, era preciso REPROVÁ-LO, o que mente no
// histórico: reprovado ≠ desistiu).
const desistiu = (c) => ehDesistente(c.STATUS);

// Ordem "natural" da coluna Avaliação: o que precisa de ação vem primeiro no
// crescente (não iniciada → em andamento → falta decidir → concluída → desistiu).
// O desistente vai para o fim porque não pede ação nenhuma.
const rankEstado = (c) => {
    if (desistiu(c)) return 4;
    if (c.RESULTADO) return 3;
    if (c.AVALIACAO_STATUS === 'Finalizado') return 2;
    if (c.AVALIACAO_STATUS === 'Rascunho') return 1;
    return 0;
};

function PPIChao() {
    const [processos, setProcessos] = useState([]);
    const [idProcesso, setIdProcesso] = useState('');
    const [lista, setLista] = useState([]);
    const [avaliando, setAvaliando] = useState(null);   // id do candidato
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    // Filtros de tela (o de processo é o único que vai ao servidor — o resto
    // peneira a lista já carregada, que é pequena por natureza: só aprovados na prova).
    const [filtros, setFiltros] = useState({ loja: '', vaga: '', prazo: '', avaliacao: '' });
    const [desistindo, setDesistindo] = useState(null);   // candidato no modal de desistência

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('/api/treinamento/ppi/processos', { headers: authHeaders() });
                if (r.ok) setProcessos(await r.json());
            } catch (_) { /* silencioso */ }
        })();
    }, []);

    const carregar = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            const q = idProcesso ? `?processo=${idProcesso}` : '';
            const r = await fetch(`${API}/pendentes${q}`, { headers: authHeaders() });
            if (!r.ok) throw new Error('Erro ao carregar os candidatos.');
            setLista(await r.json());
        } catch (e) { setErro(e.message); }
        finally { setCarregando(false); }
    }, [idProcesso]);

    useEffect(() => { carregar(); }, [carregar]);

    // Opções dos selects saem da PRÓPRIA lista — não adianta oferecer uma loja
    // que não tem ninguém aprovado na prova.
    const lojas = useMemo(() => opcoes(lista, 'LOJA_ATUAL'), [lista]);
    const vagas = useMemo(() => opcoes(lista, 'VAGA'), [lista]);

    const filtrada = useMemo(() => lista.filter((c) => {
        if (filtros.loja && c.LOJA_ATUAL !== filtros.loja) return false;
        if (filtros.vaga && c.VAGA !== filtros.vaga) return false;
        if (filtros.prazo === 'pronto' && !c.janela?.pronto) return false;
        if (filtros.prazo === 'dentro' && c.janela?.pronto) return false;
        // "Avaliado" = avaliação FINALIZADA (com ou sem decisão do RH). Rascunho
        // conta como não avaliado — a avaliação ainda não fechou.
        if (filtros.avaliacao === 'sim' && c.AVALIACAO_STATUS !== 'Finalizado') return false;
        // "Não avaliado" é a fila de trabalho, então NÃO inclui desistente: ele
        // nunca vai ser avaliado, e deixá-lo aí inflaria a fila do Treinamento.
        if (filtros.avaliacao === 'nao' && (c.AVALIACAO_STATUS === 'Finalizado' || desistiu(c))) return false;
        if (filtros.avaliacao === 'desistiu' && !desistiu(c)) return false;
        return true;
    }), [lista, filtros]);

    // Duas colunas não são campo do banco (o prazo vem de um objeto aninhado e o
    // estado da avaliação é derivado). O hook ordena por `linha[campo]`, então
    // achatamos as duas aqui, só para a ordenação.
    const linhas = useMemo(() => filtrada.map((c) => ({
        ...c,
        _DIAS: c.janela?.diasDesde ?? null,
        _ESTADO: rankEstado(c),
    })), [filtrada]);

    const { dadosOrdenados, ordenar, indicador, ordenacao } =
        useOrdenacaoTabela(linhas, TIPOS_ORDENACAO, { corAtiva: CORES.primaria });

    const { paginados, pagina, setPagina, totalPaginas, inicio, total } =
        usePaginacao(dadosOrdenados, ITENS_POR_PAGINA);

    // Filtrar ou reordenar joga a pessoa para a 1ª página — senão ela fica
    // olhando uma página 4 que não existe mais no resultado novo.
    useEffect(() => { setPagina(1); }, [idProcesso, filtros, ordenacao, setPagina]);

    if (avaliando) {
        return <Avaliacao idCandidato={avaliando} onVoltar={() => { setAvaliando(null); carregar(); }} />;
    }

    // Os KPIs acompanham o FILTRO (não a página) — é o retrato do que está à
    // vista. Filtrar por loja e ver o número da rede toda confundiria.
    // ⚠️ Desistente sai de "A avaliar" e de "Já passaram dos 30 dias": esses dois
    // números são FILA DE TRABALHO, e quem desistiu nunca vai ser avaliado. Contá-lo
    // faria o Treinamento perseguir uma pendência que não existe.
    const pendentes = filtrada.filter((c) => !c.RESULTADO && !desistiu(c)).length;
    const prontos = filtrada.filter((c) => !c.RESULTADO && !desistiu(c) && c.janela?.pronto).length;
    const concluidos = filtrada.filter((c) => c.RESULTADO).length;
    const desistentes = filtrada.filter(desistiu).length;

    const alterarFiltro = (campo, valor) => setFiltros((f) => ({ ...f, [campo]: valor }));
    const temFiltro = !!(filtros.loja || filtros.vaga || filtros.prazo || filtros.avaliacao);

    return (
        <div>
            <div style={estilos.barraFiltros}>
                <div>
                    <label style={estilos.rotuloFiltro}>Processo</label>
                    <select style={estilos.input} value={idProcesso} onChange={(e) => setIdProcesso(e.target.value)}>
                        <option value="">Todos os processos</option>
                        {processos.map((p) => <option key={p.ID} value={p.ID}>{p.NOME_PROCESSO}</option>)}
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Loja</label>
                    <select style={estilos.input} value={filtros.loja} onChange={(e) => alterarFiltro('loja', e.target.value)}>
                        <option value="">Todas as lojas</option>
                        {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Vaga</label>
                    <select style={estilos.input} value={filtros.vaga} onChange={(e) => alterarFiltro('vaga', e.target.value)}>
                        <option value="">Todas as vagas</option>
                        {vagas.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Prazo dos 30 dias</label>
                    <select style={estilos.input} value={filtros.prazo} onChange={(e) => alterarFiltro('prazo', e.target.value)}>
                        <option value="">Todos</option>
                        <option value="pronto">Já passaram dos 30 dias</option>
                        <option value="dentro">Ainda dentro dos 30 dias</option>
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Avaliação</label>
                    <select style={estilos.input} value={filtros.avaliacao} onChange={(e) => alterarFiltro('avaliacao', e.target.value)}>
                        <option value="">Todas</option>
                        <option value="sim">Avaliado</option>
                        <option value="nao">Não avaliado</option>
                        <option value="desistiu">Desistiu</option>
                    </select>
                </div>
                {temFiltro && (
                    <button type="button"
                        onClick={() => setFiltros({ loja: '', vaga: '', prazo: '', avaliacao: '' })}
                        style={{
                            background: 'none', border: 'none', color: CORES.primaria, cursor: 'pointer',
                            fontSize: '13px', fontWeight: 700, padding: '10px 0', textAlign: 'left',
                        }}>
                        limpar filtros
                    </button>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', marginBottom: '22px' }}>
                <CartaoKpi titulo="A avaliar" valor={pendentes} icone={ClipboardCheck} cor={CORES.primaria} />
                <CartaoKpi titulo="Já passaram dos 30 dias" valor={prontos} icone={Clock} cor="#b45309" />
                <CartaoKpi titulo="PPI concluído" valor={concluidos} icone={CheckCircle2} cor="#059669" />
                {/* SEMPRE visível, mesmo zerado (2026-08-04, a pedido da Julia). Um card
                    que aparece e some tem dois problemas: "zero desistências" é uma
                    informação legítima do processo, e a ausência do card é ambígua
                    (não sei se é zero ou se a medida não existe); e a grade é
                    auto-fit, então 3 ou 4 cards muda a largura de todos, fazendo a
                    linha "dançar" conforme o filtro. */}
                <CartaoKpi titulo="Desistiram" valor={desistentes} icone={UserMinus} cor={CORES.textoSec} />
            </div>

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {carregando && <p style={{ color: CORES.textoFraco }}>Carregando…</p>}
            {!carregando && !lista.length && (
                <p style={{ color: CORES.textoFraco }}>
                    Ninguém aprovado na prova ainda — a LIVE só aparece depois que o RH aprova o candidato no ranking.
                </p>
            )}
            {!carregando && !!lista.length && !filtrada.length && (
                <p style={{ color: CORES.textoFraco }}>
                    Nenhum candidato com esses filtros — os {lista.length} da lista ficaram de fora.
                </p>
            )}

            {!!filtrada.length && (
                <div style={estilos.tabelaWrap}>
                    <table style={estilos.tabela}>
                        <thead><tr>
                            <ThOrd campo="NOME" ordenar={ordenar} indicador={indicador}>Candidato</ThOrd>
                            <ThOrd campo="NOME_PROCESSO" ordenar={ordenar} indicador={indicador}>Processo</ThOrd>
                            <ThOrd campo="LOJA_ATUAL" ordenar={ordenar} indicador={indicador}>Loja</ThOrd>
                            <ThOrd campo="VAGA" ordenar={ordenar} indicador={indicador}>Vaga</ThOrd>
                            <ThOrd campo="_DIAS" ordenar={ordenar} indicador={indicador}>Prazo dos 30 dias</ThOrd>
                            <ThOrd campo="_ESTADO" ordenar={ordenar} indicador={indicador}>Avaliação</ThOrd>
                            <th style={{ ...estilos.th, textAlign: 'right' }}></th>
                        </tr></thead>
                        <tbody>
                            {paginados.map((c) => (
                                <tr key={c.ID}>
                                    <td style={estilos.td}>
                                        <strong>{c.NOME}</strong>
                                        <div style={{ fontSize: '12px', color: CORES.textoFraco }}>{c.CARGO_ATUAL}</div>
                                    </td>
                                    {/* Com o filtro em "Todos os processos", a lista mistura edições —
                                        sem esta coluna não dá para saber de qual PPI é o candidato. */}
                                    <td style={estilos.td}>
                                        {c.NOME_PROCESSO || '—'}
                                        {c.CODIGO_PROCESSO && (
                                            <div style={{ fontSize: '12px', color: CORES.textoFraco }}>{c.CODIGO_PROCESSO}</div>
                                        )}
                                    </td>
                                    <td style={estilos.td}>{c.LOJA_ATUAL}</td>
                                    <td style={estilos.td}>{c.VAGA}</td>
                                    <td style={estilos.td}><Prazo janela={c.janela} /></td>
                                    <td style={estilos.td}><EstadoAvaliacao c={c} /></td>
                                    <td style={{ ...estilos.td, textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                            {/* Desistente: a única ação é desfazer. Não faz sentido
                                                oferecer "Avaliar" — o backend recusa (409). */}
                                            {desistiu(c) ? (
                                                <BotaoDesfazerDesistencia
                                                    idCandidato={c.ID} nome={c.NOME}
                                                    onPronto={carregar} onErro={setErro} />
                                            ) : (
                                                <>
                                                    <Botao variant={c.RESULTADO ? 'secundario' : 'primario'} icone={ClipboardCheck}
                                                        onClick={() => setAvaliando(c.ID)}>
                                                        {c.RESULTADO ? 'Ver' : c.ID_AVALIACAO ? 'Continuar' : 'Avaliar'}
                                                    </Botao>
                                                    {/* Depois de concluído não dá para desistir — o resultado
                                                        já foi comunicado à pessoa (o backend também barra). */}
                                                    {!c.RESULTADO && <BotaoDesistir onClick={() => setDesistindo(c)} />}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <RodapePaginacao
                        pagina={pagina} totalPaginas={totalPaginas} inicio={inicio}
                        quantidade={paginados.length} total={total}
                        onPagina={setPagina} rotulo="candidato(s)" />
                </div>
            )}

            {desistindo && (
                <ModalDesistencia
                    idCandidato={desistindo.ID} nome={desistindo.NOME} detalhe={desistindo.VAGA}
                    onFechar={() => setDesistindo(null)}
                    onPronto={() => { setDesistindo(null); carregar(); }} />
            )}
        </div>
    );
}

// Cabeçalho clicável: o ⇅ apagado avisa que a coluna é ordenável mesmo antes do
// primeiro clique (didática herdada das tabelas do Financeiro).
const ThOrd = ({ campo, ordenar, indicador, children }) => (
    <th style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => ordenar(campo)} title="Ordenar por esta coluna">
        {children}{indicador(campo)}
    </th>
);


// O prazo é INFORMATIVO — nunca bloqueia o botão (a janela é sugestão).
const Prazo = ({ janela }) => {
    if (!janela || janela.diasDesde == null) return <span style={{ color: CORES.textoFraco }}>—</span>;
    if (janela.pronto) {
        return <span style={{ color: '#047857', fontWeight: 600, fontSize: '13px' }}>
            pronto há {janela.diasDesde - 30} dia(s)
        </span>;
    }
    return <span style={{ color: '#b45309', fontWeight: 600, fontSize: '13px' }}>
        faltam {janela.diasRestantes} dia(s)
    </span>;
};

const EstadoAvaliacao = ({ c }) => {
    // Desistência vem PRIMEIRO: ela substitui qualquer estado de avaliação. Alguém
    // pode desistir com a avaliação já finalizada e sem decisão — e aí mostrar
    // "falta decidir" mandaria o Treinamento decidir algo que não existe mais.
    // Cinza (neutro), NÃO vermelho: desistir não é reprovar (ver PPIDesistencia).
    if (desistiu(c)) return <MarcaDesistiu motivo={c.DESISTENCIA_MOTIVO} />;
    if (c.RESULTADO) {
        return <BadgeStatus tom={c.RESULTADO === 'Aprovado' ? 'ativo' : 'reprovado'}>
            PPI {c.RESULTADO}
        </BadgeStatus>;
    }
    if (c.AVALIACAO_STATUS === 'Finalizado') {
        return <span style={{ fontSize: '13px', color: CORES.textoSec }}>
            Avaliada ({fmtPct(c.PERCENTUAL)}) — <strong>falta decidir</strong>
        </span>;
    }
    if (c.AVALIACAO_STATUS === 'Rascunho') {
        return <BadgeStatus tom="rascunho">Em andamento</BadgeStatus>;
    }
    return <span style={{ fontSize: '13px', color: CORES.textoFraco }}>não iniciada</span>;
};

const fmtPct = (v) => v == null ? '—' : `${Number(v).toFixed(1).replace('.', ',')}%`;

// ─── A avaliação em si ───────────────────────────────────────────────────────
function Avaliacao({ idCandidato, onVoltar }) {
    const [dados, setDados] = useState(null);
    const [respostas, setRespostas] = useState({});   // { idPergunta: {atende, naoAplicavel, observacao} }
    const [cabecalho, setCabecalho] = useState({ dataAvaliacao: new Date().toISOString().slice(0, 10), avaliador: '', observacaoGeral: '' });
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const [msg, setMsg] = useState('');

    const carregar = useCallback(async () => {
        setErro('');
        try {
            const r = await fetch(`${API}/avaliacao/candidato/${idCandidato}`, { headers: authHeaders() });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setDados(d);
            const mapa = {};
            (d.respostas || []).forEach((x) => {
                mapa[x.ID_PERGUNTA] = { atende: !!x.ATENDE, naoAplicavel: !!x.NAO_APLICAVEL, observacao: x.OBSERVACAO || '' };
            });
            setRespostas(mapa);
            if (d.avaliacao) {
                setCabecalho({
                    dataAvaliacao: String(d.avaliacao.DATA_AVALIACAO || '').slice(0, 10),
                    avaliador: d.avaliacao.AVALIADOR || '',
                    observacaoGeral: d.avaliacao.OBSERVACAO_GERAL || '',
                });
            }
        } catch (e) { setErro(e.message); }
    }, [idCandidato]);

    useEffect(() => { carregar(); }, [carregar]);

    const responder = (idPergunta, campo, valor) => setRespostas((r) => ({
        ...r,
        [idPergunta]: {
            ...(r[idPergunta] || {}),
            ...(campo === 'atende' ? { atende: valor, naoAplicavel: false }
                : campo === 'naoAplicavel' ? { naoAplicavel: valor, atende: false }
                    : { [campo]: valor }),
        },
    }));

    const enviar = async (rascunho) => {
        setSalvando(true); setErro(''); setMsg('');
        try {
            const r = await fetch(`${API}/avaliacao`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({
                    idCandidato, idVersao: dados.versao.ID, rascunho,
                    ...cabecalho,
                    respostas: Object.entries(respostas).map(([idPergunta, v]) => ({
                        idPergunta: Number(idPergunta), ...v,
                    })),
                }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setMsg(rascunho ? 'Rascunho salvo.' : `Avaliação finalizada — ${fmtPct(d.percentual)}.`);
            await carregar();
        } catch (e) { setErro(e.message); }
        finally { setSalvando(false); }
    };

    const decidir = async (resultado) => {
        const obs = window.prompt(`Observação da decisão (${resultado}) — opcional:`) ?? '';
        if (!window.confirm(`Confirma ${resultado.toUpperCase()} no PPI? Esta é a decisão final.`)) return;
        setSalvando(true); setErro('');
        try {
            const r = await fetch(`${API}/avaliacao/${dados.avaliacao.ID}/resultado`, {
                method: 'PATCH', headers: authHeaders(),
                body: JSON.stringify({ resultado, observacao: obs }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setMsg(d.message);
            await carregar();
        } catch (e) { setErro(e.message); }
        finally { setSalvando(false); }
    };

    if (erro && !dados) {
        return (
            <div>
                <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
                <div style={{ backgroundColor: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '10px', padding: '16px', display: 'flex', gap: '10px' }}>
                    <AlertTriangle size={18} color="#be123c" style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span style={{ color: '#be123c', fontSize: '14px' }}>{erro}</span>
                </div>
            </div>
        );
    }
    if (!dados) return <p style={{ color: CORES.textoFraco }}>Carregando…</p>;

    const finalizada = dados.avaliacao?.STATUS === 'Finalizado';
    const decidida = !!dados.avaliacao?.RESULTADO;
    const totalPerguntas = dados.areas.reduce((s, a) => s + a.perguntas.length, 0);
    const respondidas = dados.areas
        .flatMap((a) => a.perguntas)
        .filter((p) => respostas[p.ID] && (respostas[p.ID].atende !== undefined || respostas[p.ID].naoAplicavel)).length;

    // Espelho visual da nota (o cálculo que VALE é o do servidor — o front não manda pontuação).
    const previa = (() => {
        let score = 0, aplicavel = 0;
        dados.areas.flatMap((a) => a.perguntas).forEach((p) => {
            const r = respostas[p.ID];
            if (!r || r.naoAplicavel) return;
            aplicavel += Number(p.PESO);
            if (r.atende) score += Number(p.PESO);
        });
        return aplicavel > 0 ? Math.round((score / aplicavel) * 1000) / 10 : null;
    })();

    return (
        <div>
            <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
                <div>
                    <h3 style={{ ...estilos.tituloSecao, marginBottom: '4px' }}>{dados.candidato.NOME}</h3>
                    <p style={{ fontSize: '13px', color: CORES.textoFraco, margin: 0 }}>
                        {dados.candidato.LOJA_ATUAL} · {dados.candidato.CARGO_ATUAL} → <strong>{dados.candidato.VAGA}</strong>
                        {' · '}{dados.candidato.NOME_PROCESSO}
                    </p>
                    <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '4px 0 0 0' }}>
                        Formulário: <strong>{dados.versao.NOME_VERSAO}</strong> · <Prazo janela={dados.janela} />
                    </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '30px', fontWeight: 900, color: CORES.primaria, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                        {previa == null ? '—' : `${String(previa).replace('.', ',')}%`}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: CORES.textoFraco, marginTop: '3px' }}>
                        conformidade · {respondidas}/{totalPerguntas}
                    </div>
                </div>
            </div>

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {msg && <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '10px 12px', borderRadius: '8px', marginBottom: '14px', fontSize: '14px' }}>{msg}</div>}

            {/* Cabeçalho da avaliação */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                <div>
                    <label style={estilos.rotuloFiltro}>Data da avaliação</label>
                    <input type="date" style={estilos.input} value={cabecalho.dataAvaliacao} disabled={finalizada}
                        onChange={(e) => setCabecalho((c) => ({ ...c, dataAvaliacao: e.target.value }))} />
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Avaliador (quem fez a LIVE)</label>
                    <input style={estilos.input} value={cabecalho.avaliador} disabled={finalizada}
                        onChange={(e) => setCabecalho((c) => ({ ...c, avaliador: e.target.value }))} />
                </div>
            </div>

            {/* Áreas → perguntas */}
            {dados.areas.map((a) => (
                <div key={a.ID} style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '16px', marginBottom: '14px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: 800, color: CORES.texto, margin: '0 0 12px 0' }}>{a.NOME}</h4>
                    {a.perguntas.map((p) => {
                        const r = respostas[p.ID] || {};
                        return (
                            <div key={p.ID} style={{ padding: '10px 0', borderTop: `1px solid ${CORES.borda}` }}>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                    <span style={{ flex: 1, minWidth: '240px', fontSize: '14px', color: CORES.texto }}>
                                        {p.TEXTO}
                                        {p.PESO > 1 && (
                                            <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 800, color: CORES.textoFraco }}>
                                                peso {p.PESO}
                                            </span>
                                        )}
                                    </span>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <BotaoResp ativo={r.atende === true && !r.naoAplicavel} cor="#059669" icone={CheckCircle2}
                                            onClick={() => responder(p.ID, 'atende', true)} disabled={finalizada}>Sim</BotaoResp>
                                        <BotaoResp ativo={r.atende === false && !r.naoAplicavel && r.atende !== undefined} cor="#e11d48" icone={XCircle}
                                            onClick={() => responder(p.ID, 'atende', false)} disabled={finalizada}>Não</BotaoResp>
                                        <BotaoResp ativo={!!r.naoAplicavel} cor="#64748b" icone={MinusCircle}
                                            onClick={() => responder(p.ID, 'naoAplicavel', !r.naoAplicavel)} disabled={finalizada}>N/A</BotaoResp>
                                    </div>
                                </div>
                                {(r.atende === false || r.observacao) && !r.naoAplicavel && (
                                    <input style={{ ...estilos.input, marginTop: '8px', fontSize: '13px' }}
                                        placeholder="Observação (o que foi visto)" value={r.observacao || ''} disabled={finalizada}
                                        onChange={(e) => responder(p.ID, 'observacao', e.target.value)} />
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}

            <div style={{ marginBottom: '18px' }}>
                <label style={estilos.rotuloFiltro}>Observação geral</label>
                <textarea style={{ ...estilos.input, minHeight: '80px', resize: 'vertical' }} value={cabecalho.observacaoGeral}
                    disabled={finalizada} onChange={(e) => setCabecalho((c) => ({ ...c, observacaoGeral: e.target.value }))} />
            </div>

            {/* Ações */}
            {!finalizada && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <Botao variant="secundario" icone={Save} onClick={() => enviar(true)} disabled={salvando}>Salvar rascunho</Botao>
                    <Botao icone={Send} onClick={() => enviar(false)} disabled={salvando}>Finalizar avaliação</Botao>
                </div>
            )}

            {/* A DECISÃO — é ela que fecha o PPI. A nota informa; quem aprova é a pessoa. */}
            {finalizada && !decidida && (
                <div style={{ border: `2px solid ${CORES.primaria}`, borderRadius: '12px', padding: '18px', backgroundColor: '#fff5f5' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: 800, color: CORES.texto, margin: '0 0 4px 0' }}>Decisão final do PPI</h4>
                    <p style={{ fontSize: '13px', color: CORES.textoSec, margin: '0 0 14px 0' }}>
                        A avaliação fechou em <strong>{fmtPct(dados.avaliacao.PERCENTUAL)}</strong> de conformidade.
                        Não há nota de corte automática — <strong>a aprovação é sua</strong>.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <Botao icone={CheckCircle2} onClick={() => decidir('Aprovado')} disabled={salvando}>Aprovar no PPI</Botao>
                        <Botao variant="perigo" icone={XCircle} onClick={() => decidir('Reprovado')} disabled={salvando}>Reprovar</Botao>
                    </div>
                </div>
            )}

            {decidida && (
                <div style={{ borderRadius: '12px', padding: '16px', backgroundColor: dados.avaliacao.RESULTADO === 'Aprovado' ? '#ecfdf5' : '#fff1f2' }}>
                    <strong style={{ color: dados.avaliacao.RESULTADO === 'Aprovado' ? '#047857' : '#be123c', fontSize: '15px' }}>
                        PPI concluído — candidato {dados.avaliacao.RESULTADO.toLowerCase()}
                        {' '}({fmtPct(dados.avaliacao.PERCENTUAL)} de conformidade).
                    </strong>
                    {dados.avaliacao.RESULTADO_OBS && (
                        <p style={{ fontSize: '13px', color: CORES.textoSec, margin: '6px 0 0 0' }}>{dados.avaliacao.RESULTADO_OBS}</p>
                    )}
                </div>
            )}
        </div>
    );
}

const BotaoResp = ({ ativo, cor, icone: Icone, children, onClick, disabled }) => (
    <button onClick={onClick} disabled={disabled}
        style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
            borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
            border: `1px solid ${ativo ? cor : CORES.borda}`,
            backgroundColor: ativo ? cor : '#fff',
            color: ativo ? '#fff' : CORES.textoSec,
            opacity: disabled && !ativo ? 0.5 : 1,
        }}>
        <Icone size={14} /> {children}
    </button>
);

const btnVoltar = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: CORES.textoSec, cursor: 'pointer', fontSize: '14px', fontWeight: 600, marginBottom: '16px', padding: 0 };

export default PPIChao;
